import React, { useState, useEffect } from 'react';
import { getCurrentLocale } from '../services/api';
import { 
  shouldShowConsentBanner, 
  acceptAllCookies, 
  rejectAllCookies, 
  saveConsentPreferences 
} from '../utils/consent';
import { useGA4 } from '../hooks/useGA4';

interface ConsentBannerProps {
  onConsentGiven?: () => void;
}

interface ConsentTexts {
  title: string;
  description: string;
  acceptAll: string;
  rejectAll: string;
  customize: string;
  save: string;
  close: string;
  essential: string;
  essentialDescription: string;
  analytics: string;
  analyticsDescription: string;
  marketing: string;
  marketingDescription: string;
  functional: string;
  functionalDescription: string;
  privacyPolicy: string;
}

const translations: Record<'nl' | 'ar', ConsentTexts> = {
  nl: {
    title: 'Cookie-instellingen',
    description: 'Wij gebruiken cookies om uw ervaring te verbeteren, verkeer te analyseren en gepersonaliseerde inhoud te leveren. U kunt uw voorkeuren hieronder aanpassen.',
    acceptAll: 'Alles accepteren',
    rejectAll: 'Alles weigeren',
    customize: 'Aanpassen',
    save: 'Opslaan',
    close: 'Sluiten',
    essential: 'Essentiële cookies',
    essentialDescription: 'Deze cookies zijn noodzakelijk voor het functioneren van de website.',
    analytics: 'Analytische cookies',
    analyticsDescription: 'Helpen ons te begrijpen hoe bezoekers onze website gebruiken.',
    marketing: 'Marketing cookies',
    marketingDescription: 'Gebruikt voor gepersonaliseerde advertenties en marketing.',
    functional: 'Functionele cookies',
    functionalDescription: 'Verbeteren de functionaliteit en personalisatie van de website.',
    privacyPolicy: 'Privacybeleid'
  },
  ar: {
    title: 'إعدادات ملفات تعريف الارتباط',
    description: 'نستخدم ملفات تعريف الارتباط لتحسين تجربتك وتحليل حركة المرور وتقديم محتوى مخصص. يمكنك تخصيص تفضيلاتك أدناه.',
    acceptAll: 'قبول الكل',
    rejectAll: 'رفض الكل',
    customize: 'تخصيص',
    save: 'حفظ',
    close: 'إغلاق',
    essential: 'ملفات تعريف الارتباط الأساسية',
    essentialDescription: 'هذه الملفات ضرورية لعمل الموقع.',
    analytics: 'ملفات تعريف الارتباط التحليلية',
    analyticsDescription: 'تساعدنا على فهم كيفية استخدام الزوار لموقعنا.',
    marketing: 'ملفات تعريف الارتباط التسويقية',
    marketingDescription: 'تُستخدم للإعلانات والتسويق المخصص.',
    functional: 'ملفات تعريف الارتباط الوظيفية',
    functionalDescription: 'تحسن وظائف الموقع والتخصيص.',
    privacyPolicy: 'سياسة الخصوصية'
  }
};

const ConsentBanner: React.FC<ConsentBannerProps> = ({ onConsentGiven }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState({
    analytics: false,
    marketing: false,
    functional: false,
  });

  const locale = getCurrentLocale();
  const texts = translations[locale];
  const isRTL = locale === 'ar';
  const { trackConsentDecision } = useGA4();

  useEffect(() => {
    setIsVisible(shouldShowConsentBanner());
  }, []);

  const handleAcceptAll = () => {
    acceptAllCookies();
    trackConsentDecision('granted', ['analytics', 'marketing', 'functional']);
    setIsVisible(false);
    onConsentGiven?.();
  };

  const handleRejectAll = () => {
    rejectAllCookies();
    trackConsentDecision('denied', ['analytics', 'marketing', 'functional']);
    setIsVisible(false);
    onConsentGiven?.();
  };

  const handleCustomize = () => {
    setShowCustomize(true);
  };

  const handleSaveCustom = () => {
    saveConsentPreferences(preferences);
    
    // Track which categories were granted/denied
    const grantedCategories = Object.entries(preferences)
      .filter(([_, granted]) => granted)
      .map(([category, _]) => category);
    const deniedCategories = Object.entries(preferences)
      .filter(([_, granted]) => !granted)
      .map(([category, _]) => category);
    
    if (grantedCategories.length > 0) {
      trackConsentDecision('granted', grantedCategories);
    }
    if (deniedCategories.length > 0) {
      trackConsentDecision('denied', deniedCategories);
    }
    
    setIsVisible(false);
    setShowCustomize(false);
    onConsentGiven?.();
  };

  const handleClose = () => {
    setShowCustomize(false);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      {showCustomize && (
        <div 
          className="consent-backdrop"
          onClick={handleClose}
        />
      )}

      {/* Main consent banner */}
      <div className={`consent-banner ${isRTL ? 'rtl' : ''}`}>
        <div className="consent-banner-content">
          <div className="consent-banner-text">
            <h3 className="consent-banner-title">{texts.title}</h3>
            <p className="consent-banner-description">{texts.description}</p>
          </div>
          
          <div className="consent-banner-actions">
            <button 
              className="btn btn-secondary consent-btn"
              onClick={handleRejectAll}
            >
              {texts.rejectAll}
            </button>
            <button 
              className="btn btn-tertiary consent-btn"
              onClick={handleCustomize}
            >
              {texts.customize}
            </button>
            <button 
              className="btn btn-primary consent-btn"
              onClick={handleAcceptAll}
            >
              {texts.acceptAll}
            </button>
          </div>
        </div>
      </div>

      {/* Customization modal */}
      {showCustomize && (
        <div className={`consent-modal ${isRTL ? 'rtl' : ''}`}>
          <div className="consent-modal-content">
            <div className="consent-modal-header">
              <h3 className="consent-modal-title">{texts.title}</h3>
              <button 
                className="consent-modal-close"
                onClick={handleClose}
                aria-label={texts.close}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path 
                    d="M18 6L6 18M6 6L18 18" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div className="consent-modal-body">
              <p className="consent-modal-description">{texts.description}</p>

              <div className="consent-categories">
                {/* Essential cookies - always enabled */}
                <div className="consent-category">
                  <div className="consent-category-header">
                    <div className="consent-category-info">
                      <h4 className="consent-category-title">{texts.essential}</h4>
                      <p className="consent-category-description">{texts.essentialDescription}</p>
                    </div>
                    <div className="consent-toggle">
                      <input 
                        type="checkbox" 
                        id="essential" 
                        checked={true} 
                        disabled 
                        className="consent-checkbox"
                      />
                      <label htmlFor="essential" className="consent-label"></label>
                    </div>
                  </div>
                </div>

                {/* Analytics cookies */}
                <div className="consent-category">
                  <div className="consent-category-header">
                    <div className="consent-category-info">
                      <h4 className="consent-category-title">{texts.analytics}</h4>
                      <p className="consent-category-description">{texts.analyticsDescription}</p>
                    </div>
                    <div className="consent-toggle">
                      <input 
                        type="checkbox" 
                        id="analytics" 
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences(prev => ({ ...prev, analytics: e.target.checked }))}
                        className="consent-checkbox"
                      />
                      <label htmlFor="analytics" className="consent-label"></label>
                    </div>
                  </div>
                </div>

                {/* Marketing cookies */}
                <div className="consent-category">
                  <div className="consent-category-header">
                    <div className="consent-category-info">
                      <h4 className="consent-category-title">{texts.marketing}</h4>
                      <p className="consent-category-description">{texts.marketingDescription}</p>
                    </div>
                    <div className="consent-toggle">
                      <input 
                        type="checkbox" 
                        id="marketing" 
                        checked={preferences.marketing}
                        onChange={(e) => setPreferences(prev => ({ ...prev, marketing: e.target.checked }))}
                        className="consent-checkbox"
                      />
                      <label htmlFor="marketing" className="consent-label"></label>
                    </div>
                  </div>
                </div>

                {/* Functional cookies */}
                <div className="consent-category">
                  <div className="consent-category-header">
                    <div className="consent-category-info">
                      <h4 className="consent-category-title">{texts.functional}</h4>
                      <p className="consent-category-description">{texts.functionalDescription}</p>
                    </div>
                    <div className="consent-toggle">
                      <input 
                        type="checkbox" 
                        id="functional" 
                        checked={preferences.functional}
                        onChange={(e) => setPreferences(prev => ({ ...prev, functional: e.target.checked }))}
                        className="consent-checkbox"
                      />
                      <label htmlFor="functional" className="consent-label"></label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="consent-modal-footer">
              <a href="/privacy-policy" className="consent-privacy-link">
                {texts.privacyPolicy}
              </a>
              <div className="consent-modal-actions">
                <button 
                  className="btn btn-secondary consent-btn"
                  onClick={handleClose}
                >
                  {texts.close}
                </button>
                <button 
                  className="btn btn-primary consent-btn"
                  onClick={handleSaveCustom}
                >
                  {texts.save}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConsentBanner;
